import type { ReactNode } from 'react'
import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/internationalization/cn'

export interface StatTileProps {
  label: string
  value: ReactNode
  /** Signed fraction, e.g. -0.12 for a 12% fall. Omit when there is no baseline. */
  delta?: number
  /** For metrics where down is good (e.g. meeting length). */
  invertDelta?: boolean
  hint?: string
  icon?: ReactNode
  className?: string
}

export function StatTile({ label, value, delta, invertDelta, hint, icon, className }: StatTileProps) {
  const isUp = (delta ?? 0) >= 0
  const isGood = invertDelta ? !isUp : isUp
  return (
    <div className={cn('sb-panel p-4', className)}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-fg-muted">{label}</p>
        {icon && <span aria-hidden className="text-fg-subtle [&>svg]:h-4 [&>svg]:w-4">{icon}</span>}
      </div>
      <p className="mt-2 text-2xl font-semibold tabular-nums tracking-tight text-fg">{value}</p>
      <div className="mt-1 flex items-center gap-1.5">
        {delta !== undefined && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 text-sm font-medium',
              isGood ? 'text-success' : 'text-danger',
            )}
          >
            {isUp ? <ArrowUpRight aria-hidden className="h-3.5 w-3.5" /> : <ArrowDownRight aria-hidden className="h-3.5 w-3.5" />}
            {Math.abs(Math.round(delta * 100))}%
          </span>
        )}
        {hint && <span className="text-sm text-fg-subtle">{hint}</span>}
      </div>
    </div>
  )
}
